<template>
  <Header
    v-if="!submitting"
    navbarType="goBack"
    :screenTitle="screenTitle"
    @clicked="goBack()"
  />
  <Alert
    v-if="showAlert"
    :message="alertMessage"
    :alertType="alertType"
    :closable="alertClosable"
    :alertIcon="alertIcon"
    @onClose="dismissAlert"
  />
  <div class="submitting" :hidden="!submitting">
    <div class="container">
      <div class="pending">
        <img
          alt="credit limit increase pending"
          class="pending"
          :src="`${useAsset('img/credit-line/pending_limit_increase.png')}`"
        />
      </div>
      <div class="pending-content">
        <h1>Transferring money to your line of credit</h1>
        <div class="container-fluid">
          <div class="row">
            <div class="col-1">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="col-11">
              Your new amount is reflected in your total credit limit
            </div>
          </div>
          <div class="row">
            <div class="col-1">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="col-11">Remember to keep your payments up to date</div>
          </div>
          <div class="row">
            <div class="col-1">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="col-11">Your money always safe and available</div>
          </div>
        </div>
      </div>
      <div class="d-flex mt-2 floating-btn">
        <button
          class="mt-5 accept-offer-btn"
          @click="goBack()"
          :enabled="done"
          :hidden="!done"
        >
          OK
        </button>
      </div>
    </div>
  </div>
  <div class="main-body" :hidden="submitting || loading">
    <div class="container">
      <hr class="mt-1 mb-1" />
      <div class="current-limit" :hidden="submitting">
        <h3 class="muted">
          {{ t("LIMIT_INCREASE.CURRENT_LIMIT") }}:
          {{ currencyFormat(lineSummary.creditLimit) }}
        </h3>
      </div>
      <div>
        <div class="clear-card mt-4" :hidden="submitting">
          <h1 class="featured item-text-wrap">
            {{ currencyFormat(offeredLimitAmount) }}
          </h1>
          <div class="italics">
            <strong>
              {{ t("LIMIT_INCREASE.NEW_LIMIT_OFFER_AMOUNT") }}
            </strong>
          </div>
        </div>
        <div class="clear-card mt-4" :hidden="!success">
          <div class="italics">
            <strong> New limit </strong>
          </div>
          <h1 class="featured item-text-wrap">
            {{ currencyFormat(offeredLimitAmount) }}
          </h1>
        </div>
        <div class="summary-card mt-4">
          <h3>{{ t("LIMIT_INCREASE.SUMMARY_LABEL") }}</h3>
          <div class="container-fluid">
            <div class="row">
              <div class="col-1">
                <i class="fa fa-calendar"></i>
              </div>
              <div class="col-7">
                {{ t("LIMIT_INCREASE.WEEKS_TO_PAY") }}
              </div>
              <div class="col-3 justify-content-end">
                <p class="text-nowrap">
                  {{ lineSummary.paymentTerm }}
                  {{ paymentTermUnit }}
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-1">
                <i class="fa fa-calendar-check"></i>
              </div>
              <div class="col-7">
                {{ t("LIMIT_INCREASE.MAX_ANNUAL_INTEREST_RATE") }}
              </div>
              <div class="col-4 justify-content-end">
                <p class="text-nowrap">
                  {{ percentageFormat(lineSummary.annualPercentageRate) }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="gray-card mt-4">
          <p class="mb-0 advice">
            <strong>
              {{ t("LIMIT_INCREASE.REDUCE_INTEREST_ADVICE") }}
            </strong>
          </p>
        </div>
        <div class="d-flex mt-2 floating-btn">
          <button
            class="mt-5 accept-offer-btn"
            @click="increaseCreditLimit()"
            :disabled="submitting"
            :hidden="submitting"
          >
            {{ t("LIMIT_INCREASE.ACCEPT_NEW_LIMIT") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import Alert from "@/shared-components/common/Alert.vue";
import { useRoute } from "vue-router";
import { ICreditLineDetails } from "@/apps/credit-line/models/CreditLineDetails";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import Header from "@/shared-components/consumer/Header.vue";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import { Service } from "@/apps/credit-line/services/network.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import percentageFormat from "@/apps/credit-line/helpers/percentageFormatter.helper";
import { FeV2ErrorCodesType } from "@/apps/credit-line/models/IFeV2ErrorCode";
import { getErrorMessage } from "@/apps/credit-line/helpers/backCodesStrings.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";

declare const Android: AndroidInterface;

export default defineComponent({
  components: {
    Header,
    Alert,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const alertClosable = ref(true);
    const screenTitle = ref(t("LIMIT_INCREASE.TITLE"));
    const creditLineId = ref<number>();
    const submitting = ref<boolean>(false);
    const success = ref<boolean>(false);
    const done = ref<boolean>(false);
    const { fetchSummary } = useCreditLineSummary();
    let loading = ref(true);
    let alertType = ref("alert-danger");
    let alertMessage = ref("");
    let showAlert = ref(false);
    let alertIcon = ref("fa fa-exclamation-triangle");
    let deviceTag = ref(Android.getDeviceTag());
    let CreditLineService = new Service();
    let lineSummary = ref({} as ICreditLineDetails);
    let paymentTermUnit = ref<string | null>();
    let offeredLimitAmount = ref<number>();
    let currentLimitAmount = ref<number>();

    onMounted(() => {
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.LimitIncreaseOfferOpened,
        "Limit Increase",
        "Limit Increase Offer View Opened"
      );
      if (isAndroid()) {
        Android.finishedLoading();
      }
    });

    const getSummary = () => {
      fetchSummary()
        .then((summary) => {
          if (summary) {
            lineSummary.value = summary;
            creditLineId.value = summary.creditLineId;
            paymentTermUnit.value =
              summary.paymentTermUnit == "WEEK"
                ? t("WEEKS")
                : summary.paymentTermUnit;
            offeredLimitAmount.value = summary?.promotion?.offeredLimitAmount;
            currentLimitAmount.value = summary.creditLimit;
            loading.value = false;
            logAnalyticsEvent(
              AnalyticEvents.CreditLine.LimitIncreaseOfferLoaded,
              "Limit Increase",
              "Limit Increase Offer Loaded",
              {
                offeredLimitAmount: offeredLimitAmount.value,
                currentLimitAmount: currentLimitAmount.value,
              }
            );
          }
        })
        .catch();
    };

    const dismissAlert = () => {
      showAlert.value = false;
    };

    const showErrorAlert = (errorCode?: FeV2ErrorCodesType) => {
      alertType.value = "alert-danger";
      if (errorCode) {
        alertMessage.value = getErrorMessage(errorCode, t, deviceTag.value);
      } else {
        alertMessage.value =
          t("LIMIT_INCREASE.ERROR_TRY_AGAIN") + " " + deviceTag.value;
      }
      alertIcon.value = "fa fa-exclamation-triangle";
      showAlert.value = true;
      window.scrollTo(0, 0);
      submitting.value = false;
    };

    const showSuccessAlert = () => {
      alertType.value = "alert-success";
      alertMessage.value = "success";
      alertIcon.value = "fa fa-check";
      showAlert.value = true;
      window.scrollTo(0, 0);
    };

    const increaseCreditLimit = async () => {
      submitting.value = true;
      try {
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.LimitIncreaseOfferAccepted,
          "Limit Increase",
          "Limit Increase Offer Accepted",
          {
            offeredLimitAmount: offeredLimitAmount.value,
            currentLimitAmount: currentLimitAmount.value,
          }
        );
        const res = await CreditLineService.increaseCreditLimit({
          deviceTag: AuthenticationHelper.getDeviceTag(),
        });
        if (res.data?.valid) {
          success.value = true;
          setTimeout(() => {
            done.value = true;
            goBack();
          }, 2000);
        } else {
          showErrorAlert();
          logAnalyticsEvent(
            AnalyticEvents.CreditLine.LimitIncreaseOfferAcceptanceNotValid,
            "Limit Increase",
            "Limit Increase Offer Accepted but Finance Engine returned not valid"
          );
        }
      } catch (error) {
        if (error?.response.data?.errorCode) {
          showErrorAlert(error?.response.data?.errorCode);
        }
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.LimitIncreaseOfferAcceptanceAPIError,
          "Limit Increase",
          "Limit Increase Offer Accepted but Finance Engine did not accept"
        );
      }
    };

    const goBack = () => {
      Android.dismissWebView();
    };

    return {
      t,
      loading,
      deviceTag,
      creditLineId,
      done,
      submitting,
      success,
      useAsset,
      screenTitle,
      route,
      goBack,
      increaseCreditLimit,
      getSummary,
      lineSummary,
      currencyFormat,
      currentLimitAmount,
      offeredLimitAmount,
      paymentTermUnit,
      percentageFormat,
      alertClosable,
      alertIcon,
      alertMessage,
      alertType,
      dismissAlert,
      showAlert,
      showErrorAlert,
      showSuccessAlert,
    };
  },
  mounted() {
    this.getSummary();
  },
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.accept-offer-btn {
  background: $pj-color-green;
  border-radius: 11px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.8px;
  color: $pj-color-white;
  width: 90%;
  height: 40px;
  border: none;
}

.gray-card {
  gap: 10px;
  background-color: #f3f3f3;
  border-radius: 10px;
  position: fixed;
  bottom: 100px;
  z-index: 199;
  width: 90%;
  padding: 10px;
}

.advice {
  color: #19191b;
  font-weight: 400;
}

.current-limit {
  gap: 2px;
  text-align: center;
  vertical-align: top;
  margin-top: 2px;
  padding: 5px;
}

.clear-card {
  border-radius: 10px;
  gap: 24px;
  border: 2px solid #f3f3f3;
  text-align: center;
  vertical-align: middle;
  margin-top: 3px;
  padding: 10px;
}

.summary-card {
  gap: 24px;
  vertical-align: middle;
  margin-top: 30px;
  padding: 10px;
}

.clear-card .item-text-wrap {
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.featured {
  color: $pj-color-green;
  font-size: xx-large;
}

.muted {
  color: #666666;
  font-size: large;
}

.italics {
  font-style: italic;
  color: #666666;
}

.floating-btn {
  position: fixed;
  padding: 20px 30px;
  bottom: 40px;
  z-index: 200;
  width: 100%;
  height: 80px;
  font-size: 1rem;
}
.pending {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  margin: 0;
  display: block;
}
.submitting {
  background-color: #f3f9ed;
  color: #2c7040;
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  right: 0;
  top: 0;
  z-index: 10;
}
.pending-content {
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  font-size: larger;
}
</style>
